import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import type { ChartAccountVersionedDto, CreateChartAccountRequest } from '@alafec/shared';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { AccountFormDialog } from '../components/pcg/account-form-dialog';
import { AccountVersionsPanel } from '../components/pcg/account-versions-panel';
import { RuleFormDialog } from '../components/pcg/rule-form-dialog';
import { PcgExportButton } from '../components/pcg/pcg-export-button';
import { PcgImportPreview } from '../components/pcg/pcg-import-preview';
import { toast } from '../components/ui/use-toast';
import { todayIso } from '../lib/dates';
import { categoryLabel, legRoleLabel, tagLabel } from '../lib/labels';
import {
  useAccountMappingRules,
  useChartOfAccounts,
  useChartOfAccountsVersions,
} from '../lib/api/hooks/use-queries';
import {
  useCreateAccountVersion,
  useCreateRule,
  useDeleteAccount,
  useDeleteRule,
  useImportPcg,
} from '../lib/api/hooks/use-mutations';

function ChartOfAccountsPage(): JSX.Element {
  const accounts = useChartOfAccounts();
  const versions = useChartOfAccountsVersions();
  const rules = useAccountMappingRules();

  const createAccount = useCreateAccountVersion();
  const deleteAccount = useDeleteAccount();
  const createRule = useCreateRule();
  const deleteRule = useDeleteRule();
  const importPcg = useImportPcg();

  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountEdit, setAccountEdit] = useState<ChartAccountVersionedDto | null>(null);
  const [selectedAccountNum, setSelectedAccountNum] = useState<string | null>(null);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<string | null>(null);

  function handleCreateAccount(input: CreateChartAccountRequest): void {
    createAccount.mutate(input, {
      onSuccess: () => {
        toast({ title: 'Compte créé' });
        setAccountDialogOpen(false);
      },
    });
  }

  function handleConfirmDeleteAccount(): void {
    if (accountToDelete === null) return;
    deleteAccount.mutate(
      { accountNum: accountToDelete, effectiveFrom: todayIso() },
      {
        onSuccess: () => {
          toast({ title: 'Compte supprimé (prospectif)' });
          setAccountToDelete(null);
        },
        onError: () => setAccountToDelete(null),
      },
    );
  }

  if (accounts.isLoading) {
    return <Skeleton className="h-96" />;
  }

  const filteredVersions = (versions.data ?? []).filter((v) => v.accountNum === selectedAccountNum);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Plan de comptes</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="accounts">
          <TabsList>
            <TabsTrigger value="accounts">Comptes</TabsTrigger>
            <TabsTrigger value="rules">Règles de mapping</TabsTrigger>
            <TabsTrigger value="import-export">Import / Export</TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {accounts.data?.length ?? 0} comptes actifs.
              </p>
              <Button
                onClick={() => {
                  setAccountEdit(null);
                  setAccountDialogOpen(true);
                }}
              >
                Ajouter
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="md:col-span-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N°</TableHead>
                      <TableHead>Libellé</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Asset</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(accounts.data ?? []).map((a) => (
                      <TableRow
                        key={`${a.accountNum}-${a.version}`}
                        className="cursor-pointer"
                        onClick={() => setSelectedAccountNum(a.accountNum)}
                      >
                        <TableCell className="font-mono">
                          <button
                            type="button"
                            className="hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAccountNum(a.accountNum);
                            }}
                          >
                            {a.accountNum}
                          </button>
                        </TableCell>
                        <TableCell>{a.accountLib}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{a.accountType}</Badge>
                        </TableCell>
                        <TableCell>{a.assetId ?? '—'}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAccountEdit(a);
                              setAccountDialogOpen(true);
                            }}
                          >
                            Modifier
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAccountToDelete(a.accountNum);
                            }}
                          >
                            Supprimer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div>
                {selectedAccountNum ? (
                  <AccountVersionsPanel
                    accountNum={selectedAccountNum}
                    versions={filteredVersions}
                  />
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Versions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        Cliquez sur un compte pour voir son historique.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
            <AccountFormDialog
              open={accountDialogOpen}
              onOpenChange={setAccountDialogOpen}
              initial={accountEdit}
              onSubmit={handleCreateAccount}
              submitting={createAccount.isPending}
            />
          </TabsContent>

          <TabsContent value="rules" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{rules.data?.length ?? 0} règles.</p>
              <Button onClick={() => setRuleDialogOpen(true)}>Ajouter</Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Compte</TableHead>
                  <TableHead>Priorité</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rules.data ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{categoryLabel(r.category)}</TableCell>
                    <TableCell>{tagLabel(r.tag)}</TableCell>
                    <TableCell>{legRoleLabel(r.role)}</TableCell>
                    <TableCell className="font-mono">{r.account}</TableCell>
                    <TableCell>{r.priority}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() =>
                          deleteRule.mutate(
                            {
                              id: r.id,
                              effectiveFrom: new Date().toISOString().slice(0, 10),
                            },
                            {
                              onSuccess: () => toast({ title: 'Règle supprimée' }),
                            },
                          )
                        }
                      >
                        Supprimer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <RuleFormDialog
              open={ruleDialogOpen}
              onOpenChange={setRuleDialogOpen}
              submitting={createRule.isPending}
              onSubmit={(input) =>
                createRule.mutate(input, {
                  onSuccess: () => {
                    toast({ title: 'Règle créée' });
                    setRuleDialogOpen(false);
                  },
                })
              }
            />
          </TabsContent>

          <TabsContent value="import-export" className="space-y-4">
            <div className="rounded-lg border p-4">
              <h3 className="mb-2 text-sm font-semibold">Exporter le PCG actuel</h3>
              <PcgExportButton />
            </div>
            <div className="rounded-lg border p-4">
              <h3 className="mb-2 text-sm font-semibold">Importer un PCG (JSON)</h3>
              <PcgImportPreview
                current={accounts.data ?? []}
                importing={importPcg.isPending}
                onConfirm={(payload, effectiveFrom, mode) =>
                  importPcg.mutate(
                    { payload, effectiveFrom, mode },
                    {
                      onSuccess: (res) =>
                        toast({
                          title:
                            mode === 'REBUILD'
                              ? 'PCG reconstruit à l’identique'
                              : 'Import effectué',
                          description:
                            mode === 'REBUILD'
                              ? `${res.accountsAdded} comptes génériques, ${res.assetAccountsImported ?? 0} comptes par actif, ${res.assetCodesImported ?? 0} codes et ${res.rulesAdded} règles importés. ${res.entriesInvalidated ?? 0} écritures ouvertes à régénérer.`
                              : `${res.accountsAdded} ajoutés, ${res.accountsUpdated} mis à jour, ${res.rulesAdded} règles ajoutées.`,
                        }),
                    },
                  )
                }
              />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>

      <Dialog
        open={accountToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setAccountToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer le compte {accountToDelete} ?</DialogTitle>
            <DialogDescription>
              Suppression prospective à partir d'aujourd'hui : l'historique des écritures déjà
              passées sur ce compte est conservé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccountToDelete(null)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteAccount}
              disabled={deleteAccount.isPending}
            >
              {deleteAccount.isPending ? 'Suppression…' : 'Confirmer la suppression'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export const Route = createFileRoute('/chart-of-accounts')({ component: ChartOfAccountsPage });
